import { InfiniteData, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { io, Socket } from "socket.io-client";

import { customerTokenService } from "@/services/customer-auth";
import { NotificationDto } from "@/services/notification.service";
import { useCustomerAuthStore } from "@/stores/customerAuthStore";
import { toast } from "sonner";

interface PaginatedPage {
  data: NotificationDto[];
  meta: {
    page: number;
    limit: number;
    hasNextPage?: boolean;
    [key: string]: unknown;
  };
}

const API_BASE_URL =
  (import.meta.env.VITE_API_BASE_URL as string) || "/api";

const SOCKET_TARGET = API_BASE_URL.startsWith("http")
  ? new URL(API_BASE_URL)
  : null;

const SOCKET_URL = SOCKET_TARGET ? SOCKET_TARGET.origin : "";
const SOCKET_PATH = SOCKET_TARGET
  ? `${SOCKET_TARGET.pathname.replace(/\/$/, "") || ""}/socket.io`
  : `${API_BASE_URL.replace(/\/$/, "")}/socket.io`;

export const useNotificationSocket = (
  options: { isStaff?: boolean; enabled?: boolean } = {},
) => {
  const { isStaff = false, enabled = true } = options;
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const socketRef = useRef<Socket | null>(null);
  const navigate = useNavigate();
  const { logout } = useCustomerAuthStore();

  useEffect(() => {
    if (!enabled) return;

    let authObj: any = undefined;
    if (!isStaff) {
      const token = customerTokenService.getAccessToken();
      if (!token) return;
      authObj = { token };
    }

    const socket = io(`${SOCKET_URL}/notifications`, {
      path: SOCKET_PATH,
      ...(authObj ? { auth: authObj } : {}),
      withCredentials: true,
      transports: ["websocket"],
      reconnection: true,
      reconnectionDelay: 3000,
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      console.log("[NotificationSocket] Connected:", socket.id);
    });

    socket.on("disconnect", (reason) => {
      console.log("[NotificationSocket] Disconnected:", reason);
    });

    // BE emits: { notification: NotificationDto }
    socket.on(
      "notification",
      ({ notification }: { notification: NotificationDto }) => {
        let isDuplicate = false;

        // 1. Prepend new notification to the top of the first page in cache
        queryClient.setQueriesData<InfiniteData<PaginatedPage>>(
          {
            queryKey: ["notifications"],
            predicate: (query) =>
              query.state.data != null &&
              typeof query.state.data === "object" &&
              "pages" in (query.state.data as object),
          },
          (old) => {
            if (!old) return old;

            const alreadyExists = old.pages.some((page) =>
              page.data?.some((item) => item.id === notification.id),
            );
            if (alreadyExists) {
              isDuplicate = true;
              return old;
            }

            const firstPage = old.pages[0];
            return {
              ...old,
              pages: [
                {
                  ...firstPage,
                  data: [notification, ...(firstPage?.data || [])],
                },
                ...old.pages.slice(1),
              ],
            };
          },
        );

        // 2. Increment unread-count in cache only if not a duplicate
        if (!isDuplicate) {
          queryClient.setQueryData<number>(
            ["notifications", "unread-count"],
            (prev = 0) => prev + 1,
          );
        }
      },
    );

    socket.on("force_logout", async () => {
      await logout();
      queryClient.removeQueries({ queryKey: ["store", "me"] });
      toast.error(t("auth.errors.session_expired"));
      navigate("/login");
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
    // Re-run if auth changes (login/logout)
  }, [isStaff, enabled, logout, navigate, queryClient]);
};
